// Static instrument → tech requirements mapping for complete tech riders

export type MusicianPosition = 'standing' | 'sitting' | 'seated-chair' | 'stool' | 'floor' | 'mobile' | 'table-stand';

export type EventZone = 'ceremony' | 'cocktail' | 'reception' | 'dinner' | 'roaming' | 'main-stage';

export const ZONE_LABELS: Record<EventZone, string> = {
  'ceremony': 'Ceremony',
  'cocktail': 'Cocktail Hour',
  'reception': 'Reception',
  'dinner': 'Dinner',
  'roaming': 'Roaming',
  'main-stage': 'Main Stage',
};

export const ZONE_DESCRIPTIONS: Record<EventZone, string> = {
  'ceremony': 'Processional, recessional, and ceremony music',
  'cocktail': 'Background ambiance during guest mingling',
  'reception': 'Dance floor, entrances, and high-energy moments',
  'dinner': 'Ambient dining music',
  'roaming': 'Musicians moving through guest spaces',
  'main-stage': 'Primary performance area',
};

export const POSITION_LABELS: Record<MusicianPosition, string> = {
  'standing': 'Standing',
  'sitting': 'Sitting (generic)',
  'seated-chair': 'Seated (chair)',
  'stool': 'Stool',
  'floor': 'Floor/Cushion',
  'mobile': 'Mobile/Moving',
  'table-stand': 'Table/Stand Required',
};

export interface InstrumentTechSpec {
  instrument: string;
  microphone: {
    type: string;
    model: string;
    notes?: string;
  };
  diBox?: {
    required: boolean;
    type?: string;
  };
  stand?: {
    type: string;
    height?: string;
  };
  power?: {
    required: boolean;
    outlets?: number;
    notes?: string;
  };
  monitor?: {
    type: string;
    notes?: string;
  };
  cables?: string[];
  stageNotes?: string;
  defaultPosition: MusicianPosition;
}

// Position requirements detail what each position needs
export const POSITION_REQUIREMENTS: Record<MusicianPosition, string> = {
  'standing': 'Standing position - mic stand at standing height',
  'sitting': 'Seated position - armless chair provided by venue',
  'seated-chair': 'Seated on chair with back support - standard armless chair',
  'stool': 'Bar stool or tall stool with footrest required',
  'floor': 'Floor seating - cushion/mat and rug required, low mic stands',
  'mobile': 'Mobile performer - wireless mic/in-ear system recommended, clear stage path',
  'table-stand': 'Waist-height table (36") or instrument stand required',
};

export const INSTRUMENT_TECH_SPECS: Record<string, InstrumentTechSpec> = {
  // String instruments
  "Violinists": {
    instrument: "Violin",
    microphone: { type: "Clip-on condenser", model: "DPA 4099V or Audio-Technica ATM350", notes: "Position near f-hole" },
    stand: { type: "Music stand", height: "Adjustable" },
    monitor: { type: "Floor wedge or IEM", notes: "Low volume to avoid feedback" },
    cables: ["XLR (25ft)"],
    stageNotes: "Position away from drums/loud sources.",
    defaultPosition: 'standing',
  },
  "Cellists": {
    instrument: "Cello",
    microphone: { type: "Clip-on condenser", model: "DPA 4099C", notes: "Clip to bridge area" },
    stand: { type: "Music stand", height: "Seated height" },
    monitor: { type: "Floor wedge", notes: "Angled toward performer" },
    defaultPosition: 'seated-chair',
    cables: ["XLR (25ft)"],
    stageNotes: "Requires armless chair. 4ft x 4ft space minimum.",
  },
  "Harpists": {
    instrument: "Harp",
    microphone: { type: "Small diaphragm condenser pair", model: "AKG C451 or Neumann KM184", notes: "Stereo pair, one high, one low" },
    stand: { type: "Boom stand (2x)", height: "Adjustable" },
    monitor: { type: "Floor wedge", notes: "Position to the side" },
    cables: ["XLR (25ft) x2"],
    defaultPosition: 'seated-chair',
    stageNotes: "6ft x 6ft space. Floor must be level. Climate-controlled preferred.",
  },
  "Acoustic Guitarists": {
    instrument: "Acoustic Guitar",
    microphone: { type: "Small diaphragm condenser", model: "Shure SM81 or AKG C451" },
    diBox: { required: true, type: "Active DI (if pickup equipped)" },
    stand: { type: "Guitar stand + music stand" },
    monitor: { type: "Floor wedge or IEM" },
    cables: ["XLR (25ft)", "1/4\" instrument cable (15ft)"],
    stageNotes: "Footrest recommended.",
    defaultPosition: 'stool',
  },
  "Electric Guitarists": {
    instrument: "Electric Guitar",
    microphone: { type: "Dynamic", model: "Shure SM57 on amp", notes: "Off-axis positioning" },
    diBox: { required: true, type: "Active DI for direct signal" },
    stand: { type: "Guitar stand" },
    power: { required: true, outlets: 2, notes: "For amp and pedalboard" },
    monitor: { type: "Floor wedge or IEM" },
    cables: ["XLR (15ft)", "1/4\" instrument cables"],
    stageNotes: "Amp isolation box for quiet stages if needed.",
    defaultPosition: 'standing',
  },
  "Bass Guitarists": {
    instrument: "Bass Guitar",
    microphone: { type: "Dynamic", model: "Shure Beta 52A or Sennheiser e602", notes: "On amp if used" },
    diBox: { required: true, type: "Active DI (Radial J48 or similar)" },
    stand: { type: "Bass stand" },
    power: { required: true, outlets: 2 },
    monitor: { type: "Floor wedge with sub", notes: "Needs low-end response" },
    cables: ["XLR (25ft)", "1/4\" instrument cable"],
    stageNotes: "DI preferred for clean signal. Amp optional.",
    defaultPosition: 'standing',
  },
  "Sitar Players": {
    instrument: "Sitar",
    microphone: { type: "Small diaphragm condenser", model: "AKG C451 or Shure SM81", notes: "Position 12-18\" from soundhole" },
    stand: { type: "Low boom stand" },
    monitor: { type: "Floor wedge", notes: "Low volume" },
    cables: ["XLR (25ft)"],
    stageNotes: "Rugs recommended for comfort.",
    defaultPosition: 'floor',
  },
  
  // Wind instruments
  "Flautists": {
    instrument: "Flute",
    microphone: { type: "Clip-on condenser", model: "DPA 4099", notes: "Or SM81 on boom" },
    stand: { type: "Music stand + boom mic stand" },
    monitor: { type: "Floor wedge or IEM" },
    cables: ["XLR (25ft)"],
    stageNotes: "Position away from AC vents to avoid noise.",
    defaultPosition: 'standing',
  },
  "Bansuri Players": {
    instrument: "Bansuri",
    microphone: { type: "Small diaphragm condenser", model: "AKG C451 or Neumann KM184", notes: "Boom stand, capture from side" },
    stand: { type: "Low boom stand + music stand" },
    monitor: { type: "Floor wedge", notes: "Minimal volume" },
    cables: ["XLR (25ft)"],
    stageNotes: "Quiet stage environment preferred.",
    defaultPosition: 'floor',
  },
  "Trumpeters": {
    instrument: "Trumpet",
    microphone: { type: "Dynamic", model: "Shure SM57 or Sennheiser e609", notes: "Angled to reduce blast" },
    stand: { type: "Boom mic stand + music stand" },
    monitor: { type: "Floor wedge" },
    cables: ["XLR (25ft)"],
    stageNotes: "Bell aimed away from other mics when possible.",
    defaultPosition: 'standing',
  },
  "Trombonists": {
    instrument: "Trombone",
    microphone: { type: "Dynamic", model: "Shure SM57 or Sennheiser MD 421", notes: "Clip-on or boom" },
    stand: { type: "Boom mic stand + music stand" },
    monitor: { type: "Floor wedge" },
    cables: ["XLR (25ft)"],
    stageNotes: "4ft clearance in front for slide movement.",
    defaultPosition: 'standing',
  },
  "Saxophonists": {
    instrument: "Saxophone",
    microphone: { type: "Dynamic or clip-on", model: "Shure SM57, Sennheiser e608, or clip-on condenser", notes: "Position at bell" },
    stand: { type: "Boom mic stand + sax stand" },
    monitor: { type: "Floor wedge or IEM" },
    cables: ["XLR (25ft)"],
    defaultPosition: 'standing',
    stageNotes: "Space to move if performing standing.",
  },
  "Shehnai Players": {
    instrument: "Shehnai",
    microphone: { type: "Small diaphragm condenser", model: "AKG C451 or Shure SM81", notes: "12-18\" from bell" },
    stand: { type: "Boom mic stand" },
    monitor: { type: "Floor wedge", notes: "Low volume - acoustic instrument" },
    cables: ["XLR (25ft)"],
    stageNotes: "Ceremonial positioning.",
    defaultPosition: 'floor',
  },
  
  // Keyboards
  "Pianists": {
    instrument: "Piano (Acoustic)",
    microphone: { type: "Matched condenser pair", model: "AKG C414 or Neumann KM184 pair", notes: "Inside lid or above hammers" },
    stand: { type: "Boom stands (2x)", height: "Adjustable for piano lid" },
    monitor: { type: "Floor wedge or IEM" },
    cables: ["XLR (25ft) x2"],
    stageNotes: "Piano tuning required day of event. 8ft x 5ft space.",
    defaultPosition: 'seated-chair',
  },
  "Keyboard Players": {
    instrument: "Keyboard/Synth",
    microphone: { type: "N/A (direct)", model: "N/A" },
    diBox: { required: true, type: "Stereo DI (Radial ProD2 or similar)" },
    stand: { type: "Keyboard stand (X or Z-style) + music stand" },
    power: { required: true, outlets: 2, notes: "For keyboard and accessories" },
    monitor: { type: "Floor wedge or IEM" },
    cables: ["XLR (25ft) x2", "1/4\" TRS cables"],
    stageNotes: "Power strip with surge protection recommended.",
    defaultPosition: 'standing',
  },
  
  // Percussion
  "Dhol Players": {
    instrument: "Dhol",
    microphone: { type: "Dynamic", model: "Shure Beta 52A (bass side) + SM57 (treble side)", notes: "Two mics recommended" },
    stand: { type: "Short boom stands (2x)" },
    monitor: { type: "Floor wedge or IEM", notes: "Hearing protection recommended" },
    cables: ["XLR (25ft) x2"],
    stageNotes: "High SPL - position away from acoustic instruments. 6ft x 6ft space for movement.",
    defaultPosition: 'mobile',
  },
  "Tabla Players": {
    instrument: "Tabla",
    microphone: { type: "Small diaphragm condenser pair", model: "AKG C451 or Shure SM81", notes: "One per drum" },
    stand: { type: "Low boom stands (2x)" },
    monitor: { type: "Floor wedge", notes: "Low volume for acoustic clarity" },
    cables: ["XLR (25ft) x2"],
    stageNotes: "Climate-controlled - skins are humidity-sensitive.",
    defaultPosition: 'floor',
  },
  "Percussionists": {
    instrument: "Percussion (General)",
    microphone: { type: "Overhead condenser + spot mics", model: "SM81 overhead, SM57 for hand drums" },
    stand: { type: "Boom stands (2-3x)" },
    monitor: { type: "Floor wedge" },
    cables: ["XLR (25ft) x3"],
    stageNotes: "Table for small instruments. 6ft x 4ft minimum space.",
    defaultPosition: 'table-stand',
  },
  "Drummers": {
    instrument: "Drum Kit",
    microphone: { 
      type: "Drum mic kit", 
      model: "Shure DMK57-52 or similar (Kick: Beta 52A, Snare: SM57, Toms: e604, OH: SM81)", 
      notes: "5-8 channel input required" 
    },
    stand: { type: "Drum throne, hardware stands, boom stands for OH" },
    power: { required: false },
    monitor: { type: "Floor wedge or throne-mounted sub", notes: "Drummer's mix essential" },
    defaultPosition: 'seated-chair',
    cables: ["XLR (25ft) x6-8", "Drum rug"],
    stageNotes: "8ft x 8ft space. Drum rug required. Isolation if in small venue.",
  },
  "Handpan Players": {
    instrument: "Handpan",
    microphone: { type: "Clip-on or overhead condenser", model: "DPA 4099 or AKG C451", notes: "Gentle touch - sensitive instrument" },
    stand: { type: "Handpan stand + low boom mic stand" },
    monitor: { type: "Floor wedge or IEM", notes: "Very low volume" },
    cables: ["XLR (25ft)"],
    stageNotes: "Quiet stage environment preferred.",
    defaultPosition: 'seated-chair',
  },
  
  // Other
  "Dilruba Players": {
    instrument: "Dilruba",
    microphone: { type: "Small diaphragm condenser", model: "AKG C451 or DPA 4099", notes: "Position near bridge" },
    stand: { type: "Low boom stand" },
    monitor: { type: "Floor wedge", notes: "Low volume" },
    cables: ["XLR (25ft)"],
    stageNotes: "Traditional seating with cushion.",
    defaultPosition: 'floor',
  },
  "Tumbi Players": {
    instrument: "Tumbi",
    microphone: { type: "Clip-on or small condenser", model: "DPA 4099 or SM81" },
    stand: { type: "Boom mic stand" },
    defaultPosition: 'standing',
    monitor: { type: "Floor wedge" },
    cables: ["XLR (25ft)"],
    stageNotes: "High energy - space to move.",
  },
  "Vocalist": {
    instrument: "Vocals",
    microphone: { type: "Dynamic or condenser", model: "Shure SM58 (dynamic) or Neumann KMS 105 (condenser)" },
    stand: { type: "Boom mic stand with round base" },
    monitor: { type: "Floor wedge or IEM", notes: "Clear vocal mix essential" },
    cables: ["XLR (25ft)"],
    stageNotes: "Center stage positioning. Lyric sheet stand if needed.",
    defaultPosition: 'standing',
  },
  "DJ": {
    instrument: "DJ Setup",
    microphone: { type: "N/A (direct)", model: "N/A" },
    diBox: { required: true, type: "Stereo DI with ground lift" },
    stand: { type: "DJ table (6ft minimum)" },
    power: { required: true, outlets: 4, notes: "For decks, mixer, laptop, lighting" },
    monitor: { type: "DJ booth monitors (pair)", notes: "Critical for beatmatching" },
    cables: ["XLR (25ft) x2", "RCA cables", "USB cables"],
    stageNotes: "6ft table at 36\" height. Power strip with surge protection. Backdrop/facade optional.",
    defaultPosition: 'table-stand',
  },
};

// Fallback for instruments not in the list
export const DEFAULT_TECH_SPEC: InstrumentTechSpec = {
  instrument: "General Instrument",
  microphone: { type: "Dynamic or condenser", model: "Shure SM57 or SM81", notes: "Position based on instrument type" },
  stand: { type: "Boom mic stand + music stand" },
  monitor: { type: "Floor wedge" },
  cables: ["XLR (25ft)"],
  stageNotes: "Consult with musician for specific requirements.",
  defaultPosition: 'standing',
};

export function getTechSpecForInstrument(instrumentName: string): InstrumentTechSpec {
  // Try exact match first
  if (INSTRUMENT_TECH_SPECS[instrumentName]) {
    return INSTRUMENT_TECH_SPECS[instrumentName];
  }
  
  // Try partial match
  const lowerName = instrumentName.toLowerCase();
  for (const [key, spec] of Object.entries(INSTRUMENT_TECH_SPECS)) {
    if (key.toLowerCase().includes(lowerName) || lowerName.includes(key.toLowerCase().replace(' players', '').replace('ists', ''))) {
      return { ...spec, instrument: instrumentName };
    }
  }
  
  return { ...DEFAULT_TECH_SPEC, instrument: instrumentName };
}

export interface InstrumentWithPosition {
  name: string;
  position: MusicianPosition;
  zone: EventZone;
  roamingNotes?: string;
}

export interface TechRider {
  eventName: string;
  date: string;
  venue?: string;
  instruments: (InstrumentTechSpec & { position: MusicianPosition })[];
  totalInputChannels: number;
  totalPowerOutlets: number;
  generalRequirements: string[];
  stageplotNotes: string[];
  setupNotes?: string;
  positionSummary: Record<MusicianPosition, number>;
}

export function generateTechRider(
  instruments: InstrumentWithPosition[],
  eventName: string,
  date: string,
  venue?: string,
  setupNotes?: string
): TechRider {
  const specs = instruments.map(inst => ({
    ...getTechSpecForInstrument(inst.name),
    position: inst.position,
  }));
  
  // Calculate totals
  let totalChannels = 0;
  let totalPower = 0;
  const positionSummary: Record<MusicianPosition, number> = {
    'standing': 0,
    'sitting': 0,
    'seated-chair': 0,
    'stool': 0,
    'floor': 0,
    'mobile': 0,
    'table-stand': 0,
  };
  
  specs.forEach(spec => {
    // Estimate channels based on mic type
    if (spec.microphone.type.includes('pair')) totalChannels += 2;
    else if (spec.microphone.type === 'N/A (direct)') totalChannels += 0;
    else if (spec.microphone.notes?.includes('Two mics')) totalChannels += 2;
    else if (spec.microphone.notes?.includes('5-8')) totalChannels += 6;
    else totalChannels += 1;
    
    if (spec.diBox?.required) totalChannels += spec.microphone.type.includes('Stereo') ? 2 : 1;
    if (spec.power?.outlets) totalPower += spec.power.outlets;
    
    positionSummary[spec.position]++;
  });
  
  // Add 2 channels for regional manager laptop (always required)
  totalChannels += 2;
  
  const generalRequirements = [
    `Minimum ${totalChannels} input channels on mixing console`,
    `${specs.length} monitor sends (individual mixes preferred)`,
    `${totalPower > 0 ? totalPower + ' power outlets on stage' : 'Standard stage power'}`,
    'Sound check: 60-90 minutes before performance',
    'FOH engineer required for event',
    'All XLR cables to be provided by venue/production',
    '1x Stereo DI Box (Radial ProD2 or similar) for Regional Manager laptop',
    '1x 3.5mm to 1/4" TRS AUX cable (6ft minimum) for laptop connection',
  ];
  
  // Add position-specific requirements
  if (positionSummary['floor'] > 0) {
    generalRequirements.push(`${positionSummary['floor']} floor cushion(s)/mat(s) and rug(s) for traditional seating`);
  }
  if (positionSummary['seated-chair'] > 0) {
    generalRequirements.push(`${positionSummary['seated-chair']} armless chair(s) for seated musicians`);
  }
  if (positionSummary['stool'] > 0) {
    generalRequirements.push(`${positionSummary['stool']} stool(s) with footrest`);
  }
  if (positionSummary['table-stand'] > 0) {
    generalRequirements.push(`${positionSummary['table-stand']} waist-height table(s)/stand(s) (36" height)`);
  }
  if (positionSummary['mobile'] > 0) {
    generalRequirements.push(`Clear stage path for ${positionSummary['mobile']} mobile performer(s)`);
  }
  
  const stageplotNotes = [
    `${instruments.length} musician positions on stage`,
    'Music stands with lights for all acoustic musicians',
    'Climate control required for acoustic instruments',
    'Separate green room/tuning area preferred',
  ];
  
  // Add positioning breakdown
  const activePositions = Object.entries(positionSummary)
    .filter(([_, count]) => count > 0)
    .map(([pos, count]) => `${count}x ${POSITION_LABELS[pos as MusicianPosition]}`);
  if (activePositions.length > 0) {
    stageplotNotes.push(`Positioning: ${activePositions.join(', ')}`);
  }
  
  return {
    eventName,
    date,
    venue,
    instruments: specs,
    totalInputChannels: totalChannels,
    totalPowerOutlets: totalPower,
    generalRequirements,
    stageplotNotes,
    setupNotes,
    positionSummary,
  };
}

// Helper to get default positions for a list of instruments
export function getDefaultPositions(instruments: string[]): InstrumentWithPosition[] {
  return instruments.map(name => ({
    name,
    position: getTechSpecForInstrument(name).defaultPosition,
    zone: 'main-stage' as EventZone,
  }));
}

// Get equipment summary grouped by zone
export function getEquipmentByZone(instruments: InstrumentWithPosition[]): Record<EventZone, {
  musicians: { name: string; position: MusicianPosition; mic: string; di: boolean; power: number; monitor: string; notes?: string }[];
  totalChannels: number;
  totalPower: number;
  totalMonitors: number;
  equipment: string[];
}> {
  const zones: Record<EventZone, {
    musicians: { name: string; position: MusicianPosition; mic: string; di: boolean; power: number; monitor: string; notes?: string }[];
    totalChannels: number;
    totalPower: number;
    totalMonitors: number;
    equipment: string[];
  }> = {
    'ceremony': { musicians: [], totalChannels: 0, totalPower: 0, totalMonitors: 0, equipment: [] },
    'cocktail': { musicians: [], totalChannels: 0, totalPower: 0, totalMonitors: 0, equipment: [] },
    'reception': { musicians: [], totalChannels: 0, totalPower: 0, totalMonitors: 0, equipment: [] },
    'dinner': { musicians: [], totalChannels: 0, totalPower: 0, totalMonitors: 0, equipment: [] },
    'roaming': { musicians: [], totalChannels: 0, totalPower: 0, totalMonitors: 0, equipment: [] },
    'main-stage': { musicians: [], totalChannels: 0, totalPower: 0, totalMonitors: 0, equipment: [] },
  };
  
  instruments.forEach(inst => {
    const spec = getTechSpecForInstrument(inst.name);
    const zone = zones[inst.zone];
    
    // Calculate channels for this instrument
    let channels = 1;
    if (spec.microphone.type.includes('pair')) channels = 2;
    else if (spec.microphone.type === 'N/A (direct)') channels = 0;
    else if (spec.microphone.notes?.includes('Two mics')) channels = 2;
    else if (spec.microphone.notes?.includes('5-8')) channels = 6;
    if (spec.diBox?.required) channels += spec.microphone.type.includes('Stereo') ? 2 : 1;
    
    const power = spec.power?.outlets || 0;
    
    zone.musicians.push({
      name: inst.name,
      position: inst.position,
      mic: spec.microphone.type !== 'N/A (direct)' ? `${spec.microphone.model}` : 'Direct (DI)',
      di: spec.diBox?.required || false,
      power,
      monitor: spec.monitor?.type || 'Floor wedge',
      notes: inst.roamingNotes,
    });
    
    zone.totalChannels += channels;
    zone.totalPower += power;
    zone.totalMonitors += 1;
    
    // Add equipment items
    if (spec.microphone.type !== 'N/A (direct)') {
      zone.equipment.push(`Mic: ${spec.microphone.model}`);
    }
    if (spec.diBox?.required) {
      zone.equipment.push(`DI Box: ${spec.diBox.type || 'Active DI'}`);
    }
    if (spec.stand?.type) {
      zone.equipment.push(`Stand: ${spec.stand.type}`);
    }
    if (POSITION_REQUIREMENTS[inst.position]) {
      zone.equipment.push(POSITION_REQUIREMENTS[inst.position]);
    }
  });
  
  return zones;
}

// Generate positioning summary for venue/planners
export function getPositioningSummary(instruments: InstrumentWithPosition[]): string[] {
  const summaries: string[] = [];
  const byZone = getEquipmentByZone(instruments);
  
  Object.entries(byZone).forEach(([zone, data]) => {
    if (data.musicians.length === 0) return;
    
    const zoneLabel = ZONE_LABELS[zone as EventZone];
    const musicianList = data.musicians.map(m => {
      const posLabel = POSITION_LABELS[m.position];
      return `${m.name} (${posLabel})${m.notes ? ` — ${m.notes}` : ''}`;
    });
    
    summaries.push(`**${zoneLabel}**: ${musicianList.join(', ')}`);
  });
  
  return summaries;
}
